// FeedbackWidget.js – MUI version (preserving colors and UI)
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';

// Constants
const FEEDBACK_COPIES = [
    'Share your thoughts',
    'Got feedback?',
    'Tell us your ideas',
    'What do you think?',
    'Drop us a note',
    "We'd love your input",
    'Help us improve',
    'Make this better',
    'Shape the product',
    'Be part of the build',
    'Improve with us',
    'Your opinion matters',
    'Suggest a feature',
    'Report a bug',
    'Request a change',
    "Tell us what's missing",
    'What should we build next?',
    'Spill the tea ☕',
    'Roast us (nicely 😅)',
    'Have a hot take?',
    'Drop a thought 💬',
    "What's on your mind?"
];

const UI_PRESETS = {
    "small text": { render: "input" },
    "email": { render: "email" },
    "dropdown": { render: "select" },
    "options": { render: "buttons" },
    "small desc": { render: "input" },
    "long desc": { render: "textarea" }
};

// Helper Functions
const getRandomFeedbackCopy = () => {
    return FEEDBACK_COPIES[Math.floor(Math.random() * FEEDBACK_COPIES.length)];
};

const getInitialFormData = (userName, userEmail) => ({
    userName: userName ?? '',
    userEmail: userEmail ?? '',
    category: 'Feature Request',
    title: '',
    description: '',
    priority: 'Medium'
});

// FeedbackModal Component
const FeedbackModal = ({
    isOpen,
    onClose,
    feedbackLog,
    onAddFeedback,
    userName,
    userEmail
}) => {
    const [dynamicFields, setDynamicFields] = useState([]);
    const [activeTab, setActiveTab] = useState('FORM');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [formData, setFormData] = useState(() => getInitialFormData(userName, userEmail));
    const hasLoadedRef = useRef(false);
    const [isConfigLoading, setIsConfigLoading] = useState(false);

    // Load config
    useEffect(() => {
        if (hasLoadedRef.current || !isOpen) return;

        setIsConfigLoading(true);
        hasLoadedRef.current = true;

        fetch(`https://script.google.com/macros/s/AKfycbxTFB6NY9wFOy6GhAhUsi_XssgY4kZZWLzBDCM5rsucxu7BRlEOMlCZXmQfA1FFvP_l/exec?config=1&formId=discovery`)
            .then(res => res.json())
            .then(data => {
                setDynamicFields(data);
            })
            .catch(err => {
                console.error("Config load failed", err);
            })
            .finally(() => {
                setIsConfigLoading(false);
            });
    }, [isOpen]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            const dynamicDefaults = Object.fromEntries(
                dynamicFields.map(f => [f.key, f.default || ""])
            );

            setFormData(prev => ({
                ...prev,
                ...dynamicDefaults,
                userName: userName ?? prev.userName,
                userEmail: userEmail ?? prev.userEmail
            }));
            return;
        }

        setFormData(getInitialFormData(userName, userEmail));
        setShowSuccess(false);
        setIsSubmitting(false);
        setActiveTab('FORM');
    }, [isOpen, userName, userEmail, dynamicFields]);

    // Auto-close after success
    useEffect(() => {
        let timeoutId;

        if (showSuccess) {
            timeoutId = setTimeout(() => {
                onClose();
            }, 2000);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [showSuccess, onClose]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.title || !formData.description || !formData.userName) return;

        setIsSubmitting(true);

        fetch("https://script.google.com/macros/s/AKfycbxTFB6NY9wFOy6GhAhUsi_XssgY4kZZWLzBDCM5rsucxu7BRlEOMlCZXmQfA1FFvP_l/exec", {
            method: "POST",
            mode: "no-cors",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ...formData,
                formId: "transcribe"
            })
        })
            .then(() => {
                onAddFeedback({
                    ...formData,
                    id: crypto.randomUUID(),
                    timestamp: Date.now()
                });
                setIsSubmitting(false);
                setShowSuccess(true);
            })
            .catch(err => {
                console.error("Save failed", err);
                setIsSubmitting(false);
                alert("Could not save feedback.");
            });
    };

    const getPriorityStyles = (priority) => {
        switch (priority) {
            case 'Low': return { bgcolor: '#FEF3C7', color: '#D97706', borderColor: '#FDE68A' };
            case 'Medium': return { bgcolor: '#D1FAE5', color: '#059669', borderColor: '#A7F3D0' };
            case 'High': return { bgcolor: '#FEE2E2', color: '#DC2626', borderColor: '#FECACA' };
            default: return { bgcolor: '#F1F5F9', color: '#64748B', borderColor: '#E2E8F0' };
        }
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'Pending': return { bgcolor: '#F1F5F9', color: '#64748B', borderColor: '#E2E8F0' };
            case 'Reviewing': return { bgcolor: '#EFF6FF', color: '#096BEF', borderColor: '#DBEAFE' };
            case 'In Progress': return { bgcolor: '#E0F2FE', color: '#0284C7', borderColor: '#BAE6FD' };
            case 'Completed': return { bgcolor: '#D1FAE5', color: '#059669', borderColor: '#A7F3D0' };
            case 'Rejected': return { bgcolor: '#FEE2E2', color: '#DC2626', borderColor: '#FEE2E2' };
            default: return { bgcolor: '#F1F5F9', color: '#64748B' };
        }
    };

    const renderField = (f) => {
        const uiPreset = UI_PRESETS[f.ui];

        const label = (
            <Typography
                component="label"
                sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#94A3B8',
                    //   textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    mb: 0.5,
                    ml: 0.5
                }}
            >
                {f.label}
            </Typography>
        );

        if (uiPreset?.render === "buttons" || f.type === "options") {
            return (
                <Box key={f.key} sx={{ mb: 1 }}>
                    {label}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {f.options.map(option => {
                            const isPriorityField = f.key === 'priority';
                            const isSelected = formData[f.key] === option;
                            const activeStyles = isPriorityField
                                ? { ...getPriorityStyles(option), boxShadow: 1 }
                                : { bgcolor: '#096BEF', color: 'white', borderColor: '#096BEF', boxShadow: 1 };

                            return (
                                <Button
                                    key={option}
                                    type="button"
                                    variant="outlined"
                                    onClick={() => setFormData(prev => ({ ...prev, [f.key]: option }))}
                                    sx={{
                                        flex: 1,
                                        py: 1,
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        border: '1px solid',
                                        ...(isSelected
                                            ? activeStyles
                                            : {
                                                bgcolor: 'white',
                                                borderColor: '#E2E8F0',
                                                color: '#475569',
                                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                                            })
                                    }}
                                >
                                    {option}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            );
        }

        if (f.key === "priority" && f.type === "select") {
            return (
                <Box key={f.key} sx={{ mb: 1 }}>
                    {label}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {f.options.map(p => (
                            <Button
                                key={p}
                                type="button"
                                variant="outlined"
                                onClick={() => setFormData(prev => ({ ...prev, [f.key]: p }))}
                                sx={{
                                    flex: 1,
                                    py: 0.75,
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    border: '1px solid',
                                    ...(formData[f.key] === p
                                        ? { ...getPriorityStyles(p), boxShadow: 1 }
                                        : {
                                            bgcolor: 'white',
                                            borderColor: '#F1F5F9',
                                            color: '#CBD5E1',
                                            '&:hover': { borderColor: '#E2E8F0' }
                                        })
                                }}
                            >
                                {p}
                            </Button>
                        ))}
                    </Box>
                </Box>
            );
        }

        if (uiPreset?.render === "select" || f.type === "select") {
            return (
                <Box key={f.key} sx={{ mb: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id={`label-${f.key}`} sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', ml: 0.5 }}>
                            {f.label}
                        </InputLabel>
                        <Select
                            labelId={`label-${f.key}`}
                            required={f.required}
                            value={formData[f.key] || ""}
                            onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                            sx={{
                                borderRadius: '8px',
                                fontSize: '14px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#E2E8F0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#E2E8F0'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#096BEF',
                                    borderWidth: '2px'
                                }
                            }}
                        >
                            <MenuItem value="">Select...</MenuItem>
                            {f.options.map(o => (
                                <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            );
        }

        if (uiPreset?.render === "textarea" || f.type === "textarea") {
            return (
                <Box key={f.key} sx={{ mb: 1 }}>
                    {label}
                    <TextField
                        required={f.required}
                        multiline
                        rows={uiPreset?.render === "textarea" ? 4 : undefined}
                        fullWidth
                        size="small"
                        value={formData[f.key] || ""}
                        onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '14px',
                                bgcolor: 'white',
                                '& fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#096BEF',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputBase-input': {
                                resize: 'none'
                            }
                        }}
                    />
                </Box>
            );
        }

        if (uiPreset?.render === "email" || f.key === "userEmail") {
            return (
                <Box key={f.key} sx={{ mb: 1 }}>
                    {label}
                    <TextField
                        type="email"
                        required={f.required}
                        fullWidth
                        size="small"
                        value={formData[f.key] || ""}
                        onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                        InputProps={{ readOnly: f.key === "userEmail" }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '14px',
                                ...(f.key === "userEmail" ? { bgcolor: '#F8FAFC', color: '#94A3B8' } : { bgcolor: 'white' }),
                                '& fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#E2E8F0'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#096BEF',
                                    borderWidth: '2px'
                                }
                            },
                            ...(f.key === "userEmail" && {
                                '& .MuiInputBase-input': {
                                    cursor: 'not-allowed'
                                }
                            })
                        }}
                    />
                </Box>
            );
        }

        return (
            <Box key={f.key} sx={{ mb: 1 }}>
                {label}
                <TextField
                    type="text"
                    required={f.required}
                    fullWidth
                    size="small"
                    value={formData[f.key] || ""}
                    onChange={e => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '14px',
                            bgcolor: 'white',
                            '& fieldset': {
                                borderColor: '#E2E8F0'
                            },
                            '&:hover fieldset': {
                                borderColor: '#E2E8F0'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#096BEF',
                                borderWidth: '2px'
                            }
                        }
                    }}
                />
            </Box>
        );
    };

    if (!isOpen) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    // maxHeight: '650px',
                    // overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
            // slotProps={{
            //     backdrop: {
            //         sx: {
            //             bgcolor: 'rgba(15, 23, 42, 0.4)',
            //             backdropFilter: 'blur(4px)'
            //         }
            //     }
            // }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '650px', bgcolor: 'white', overflowY: 'auto', overflowX: 'hidden' }}>
                {/* Header */}
                <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                            <ChatBubbleOutlineIcon sx={{ color: '#096BEF', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', lineHeight: 1 }}>
                                Transcribe Feedback
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#94A3B8', mt: 0.5, fontWeight: 500, letterSpacing: '-0.01em', }}>
                                Product Improvements
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: '#94A3B8', p: 1, '&:hover': { bgcolor: '#F1F5F9' } }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* Tab: Submit Suggestion */}
                <Box sx={{ 
                     bgcolor: 'grey.50', flexShrink: 0, position: 'sticky', top: '72px', zIndex: 9 }}>
                    <Button
                        onClick={() => { setActiveTab('FORM'); setShowSuccess(false); }}
                        sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: '12px',
                            fontWeight: 700,
                            borderRadius: 0,
                            borderBottom: 2,
                            borderColor: activeTab === 'FORM' ? '#096BEF' : 'transparent',
                            color: activeTab === 'FORM' ? '#096BEF' : '#94A3B8',
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: activeTab === 'FORM' ? '#096BEF' : '#475569'
                            }
                        }}
                    >
                        Submit Suggestion
                    </Button>
                </Box>

                {/* Content */}
                <DialogContent sx={{ flex: 1, overflow: 'visible', display: 'flex', flexDirection: 'column', position: 'relative', p: 0, minHeight: 0 }}>
                    {activeTab === 'FORM' ? (
                        <Box sx={{ position: 'relative', bgcolor: 'white', minHeight: '100%' }}>
                            {isSubmitting && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 10,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress
                                        size={40}
                                        sx={{
                                            color: '#096BEF',
                                            mb: 2,
                                            '& .MuiCircularProgress-circle': {
                                                strokeWidth: 4
                                            }
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1E293B' }}>
                                        Saving your feedback...
                                    </Typography>
                                </Box>
                            )}

                            {isConfigLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 20,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CircularProgress
                                        size={40}
                                        sx={{
                                            color: '#096BEF',
                                            mb: 1.5,
                                            '& .MuiCircularProgress-circle': {
                                                strokeWidth: 4
                                            }
                                        }}
                                    />
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1E293B' }}>
                                        Loading feedback form...
                                    </Typography>
                                </Box>
                            )}

                            {showSuccess && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 20,
                                        bgcolor: 'white',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        p: 5
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: '#D1FAE5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                            animation: 'scaleUpCenter 0.3s ease-out',
                                            '@keyframes scaleUpCenter': {
                                                from: {
                                                    transform: 'scale(0.8)',
                                                    opacity: 0
                                                },
                                                to: {
                                                    transform: 'scale(1)',
                                                    opacity: 1
                                                }
                                            }
                                        }}
                                    >
                                        <CheckCircleIcon sx={{ color: '#10B981', fontSize: 32 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', mb: 1 }}>
                                        Submitted!
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#64748B', lineHeight: 1.75, maxWidth: 280, mx: 'auto' }}>
                                        Thanks for your feedback. We'll review it soon.
                                    </Typography>
                                </Box>
                            )}

                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{
                                    padding: '15px 24px',
                                    opacity: isSubmitting ? 0 : 1,
                                    pointerEvents: isSubmitting ? 'none' : 'auto',
                                    transition: 'opacity 0.2s ease'
                                }}
                            >
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                    {dynamicFields
                                        .filter(f => ["small text", "email", "dropdown", "options"].includes(f.ui))
                                        .map(renderField)}
                                </Box>

                                {dynamicFields
                                    .filter(f => ["small desc", "long desc"].includes(f.ui))
                                    .map(renderField)}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button
                                        type="submit"
                                        disabled={!formData.title || !formData.description || !formData.userName || isSubmitting}
                                        sx={{
                                            marginTop: '10px',
                                            py: '8px',
                                            width: { xs: '100%', sm: 'auto' },
                                            bgcolor: '#096BEF',
                                            color: 'white',
                                            borderRadius: '10px',
                                            fontSize: '12px',
                                            boxShadow: '0 10px 15px -3px rgba(9, 107, 239, 0.1), 0 4px 6px -2px rgba(9, 107, 239, 0.05)',
                                            '&:hover': {
                                                bgcolor: '#004BB5'
                                            },
                                            '&:disabled': {
                                                opacity: 0.5,
                                                cursor: 'not-allowed',
                                                bgcolor: '#096BEF',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        Submit Feedback
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, md: 3 }, bgcolor: 'rgba(248, 250, 252, 0.3)', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                    History Log
                                </Typography>
                                {feedbackLog.length > 0 && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<DescriptionIcon sx={{ fontSize: 12 }} />}
                                        sx={{
                                            bgcolor: '#1E293B',
                                            color: 'white',
                                            fontSize: '9px',
                                            fontWeight: 700,
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: '8px',
                                            '&:hover': {
                                                bgcolor: '#000000'
                                            }
                                        }}
                                    >
                                        Export CSV
                                    </Button>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <Box
                                    sx={{
                                        overflow: 'auto',
                                        flex: 1,
                                        '&::-webkit-scrollbar': {
                                            height: '6px',
                                            width: '6px'
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#F8FAFC'
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: '#E2E8F0',
                                            borderRadius: '3px',
                                            '&:hover': {
                                                background: '#CBD5E1'
                                            }
                                        }
                                    }}
                                >
                                    {feedbackLog.length === 0 ? (
                                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', opacity: 0.3, py: 6 }}>
                                            <HistoryIcon sx={{ fontSize: 48, color: '#64748B', mb: 2 }} />
                                            <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: '14px' }}>
                                                No feedback found
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Table size="small" sx={{ minWidth: 850 }}>
                                            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableCell sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 192, borderBottom: '1px solid #E2E8F0' }}>
                                                        Subject
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', borderBottom: '1px solid #E2E8F0' }}>
                                                        Description
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 96, borderBottom: '1px solid #E2E8F0' }}>
                                                        Status
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 128, borderBottom: '1px solid #E2E8F0' }}>
                                                        Category
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 80, borderBottom: '1px solid #E2E8F0' }}>
                                                        Priority
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 112, borderBottom: '1px solid #E2E8F0' }}>
                                                        Date
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: '9px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', width: 80, borderBottom: '1px solid #E2E8F0' }}>
                                                        Time



                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {feedbackLog.map(item => (
                                                    <TableRow
                                                        key={item.id}
                                                        sx={{
                                                            '&:hover': { bgcolor: 'rgba(248, 250, 252, 0.5)' },
                                                            borderBottom: '1px solid #F1F5F9'
                                                        }}
                                                    >
                                                        <TableCell sx={{ px: 2, py: 2 }}>
                                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1E293B', lineHeight: 1.25 }}>
                                                                {item.title}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ px: 2, py: 2 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '10px',
                                                                    color: '#64748B',
                                                                    fontStyle: 'italic',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 1,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    lineHeight: 1.5,
                                                                    cursor: 'help',
                                                                    maxWidth: 250
                                                                }}
                                                                title={item.description}
                                                            >
                                                                {item.description}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ px: 2, py: 2 }}>
                                                            <Chip
                                                                size="small"
                                                                icon={item.status === 'Pending' ? <ScheduleIcon sx={{ fontSize: 8, ml: 0.5 }} /> : undefined}
                                                                label={item.status === 'Completed' ? 'RESOLVED' : item.status.toUpperCase()}
                                                                sx={{
                                                                    fontSize: '8px',
                                                                    fontWeight: 700,
                                                                    height: 20,
                                                                    px: 1.25,
                                                                    py: 0.5,
                                                                    borderRadius: '9999px',
                                                                    textTransform: 'uppercase',
                                                                    border: '1px solid',
                                                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                                                    ...getStatusStyles(item.status)
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ px: 2, py: 2 }}>
                                                            <Chip
                                                                size="small"
                                                                label={item.category}
                                                                sx={{
                                                                    fontSize: '8px',
                                                                    fontWeight: 700,
                                                                    bgcolor: '#F1F5F9',
                                                                    color: '#64748B',
                                                                    border: '1px solid #E2E8F0',
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: '6px',
                                                                    textTransform: 'uppercase',
                                                                    whiteSpace: 'nowrap',
                                                                    height: 'auto'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ px: 2, py: 2 }}>
                                                            <Chip
                                                                size="small"
                                                                label={item.priority.toUpperCase()}
                                                                sx={{
                                                                    fontSize: '8px',
                                                                    fontWeight: 700,
                                                                    px: 1.25,
                                                                    py: 0.25,
                                                                    borderRadius: '9999px',
                                                                    textTransform: 'uppercase',
                                                                    border: '1px solid',
                                                                    height: 'auto',
                                                                    ...getPriorityStyles(item.priority)
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ px: 2, py: 2 }}>
                                                            <Typography sx={{ fontSize: '10px', color: '#64748B', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                                {new Date(item.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ px: 2, py: 2 }}>
                                                            <Typography sx={{ fontSize: '10px', color: '#94A3B8', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                                {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Box>
        </Dialog>
    );
};

// Main FeedbackWidget Component
export const FeedbackWidget = ({
    userName,
    userEmail,
    buttonClassName,
    buttonText,
    isDrawerOpen = true
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [feedbackButtonText, setFeedbackButtonText] = useState(
        buttonText || 'Got feedback?'
    );
    const [feedbackLog, setFeedbackLog] = useState([]);

    useEffect(() => {
        if (!isModalOpen) {
            setFeedbackButtonText(buttonText || 'Got feedback?');
        }
    }, [isModalOpen, buttonText]);

    const handleAddFeedback = (newFeedback) => {
        const newEntry = {
            ...newFeedback,
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            status: 'Pending'
        };
        setFeedbackLog(prev => [newEntry, ...prev]);
    };

    const modalEl = (
        <FeedbackModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            feedbackLog={feedbackLog}
            userName={userName}
            userEmail={userEmail}
            onAddFeedback={handleAddFeedback}
        />
    );

    // Style matching other drawer menu items
    const getFeedbackItemStyle = (isOpen, isActive = false) => {
        const activeColor = 'var(--primary-300)';
        
        return {
            minHeight: 52,
            justifyContent: isOpen ? 'initial' : 'center',
            borderRadius: '12px',
            border: isActive ? '1px solid' : '1px solid transparent',
            borderColor: isActive ? activeColor : 'transparent',
            color: isActive ? activeColor : 'text.primary',
            '&:hover': {
                borderColor: activeColor,
                color: activeColor,
                bgcolor: 'var(--grey-0)',
            },
        };
    };

    return (
        <ListItem disablePadding sx={{ display: 'block', mb: 1, px: 1.5 }}>
            {/* Tooltip only shown when drawer is collapsed */}
            <Tooltip title={isDrawerOpen ? '' : feedbackButtonText} placement="right" arrow>
                <ListItemButton
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsModalOpen(true);
                    }}
                    sx={getFeedbackItemStyle(isDrawerOpen, false)}
                    className={buttonClassName}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: isDrawerOpen ? 2 : 'auto',
                            justifyContent: 'center',
                            color: 'inherit',
                        }}
                    >
                        <ChatBubbleOutlineIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary={feedbackButtonText}
                        sx={{ opacity: isDrawerOpen ? 1 : 0 }}
                        primaryTypographyProps={{
                            fontSize: '14px',
                            fontWeight: 400,
                            whiteSpace: 'nowrap',
                        }}
                    />
                </ListItemButton>
            </Tooltip>
            {createPortal(modalEl, document.body)}
        </ListItem>
    );
};

export default FeedbackWidget;
